/**
 * SubmitSearchParam
 * 
 * @Author: Focci
 * @Date: 2023-08-29 15:14:31
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-29 15:14:31
 */

import PropTypes from 'prop-types'
import useProgressRouter from '@hook/useProgressRouter'
import useFetch from '@hook/useFetch'
import CheckIcon from '@mui/icons-material/Check'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo } from 'react'
import { times, map, isArray, forEach, delay, filter } from 'lodash-es'
import { urls } from '@lib/parse'
import AspectRatioCover from '@comp/AspectRatioCover'
import { fetchPost } from '@lib/fetch/client'
import { isValid, toSlug } from '@lib/utils'
import useSearchRecording from '@hook/useSearchRecording'

function IntroItem({ label, delayTime = 0.2 }) {
  return (
    <div
      className="relative top-2 opacity-0"
      style={{
        animationName: 'riseup',
        animationDuration: '0.5s',
        animationTimingFunction: 'ease-in-out',
        animationDelay: `${delayTime}s`,
        animationFillMode: 'forwards',
      }}
    >
      <CheckIcon className="text-primary mr-1" />
      {label}
    </div>
  )
}

export default function SubmitSearchParam({
  params,
  suburbName,
  suburbId,
  onSubmit = () => {}
}) {
  const locale = useLocale()
  const ta = useTranslations('agent')
  const { routerPush } = useProgressRouter()
  const { data } = useFetch('recommended-agent-rand', { limit: 10 })
  const { pushRecord } = useSearchRecording()
  const avatars = useMemo(() => {
    if(!isArray(data)) {
      return times(100).fill('')
    }

    const tmp = []
    while(tmp.length < 100) {
      tmp.push(...map(data, 'ossAvatar'))
    }
    return tmp
  }, [data])

  const apiParam = useMemo(() => {
    const tmp = { suburbId, suburbName, page: 1 }
    forEach(params, (p) => tmp[p.field] = p.value)

    return tmp
  }, [params, suburbId, suburbName])

  const handleSubmit = useCallback(async () => {
    await fetchPost('agent/find-agent-record', apiParam)

    data && delay(() => {
      const param = {
        page: 1,
        list: [],
        suburb: [{ label: toSlug(suburbName), value: suburbId }],
        filters: map(
          filter(params, (f) => isValid(f.value)), 
          (item) => ({
            label: item.field,
            value: item.value
          })
        )
      }

      const url = urls.agents(param, locale)
      routerPush(url)
      onSubmit({ url })
    }, 1000)
  }, [apiParam, data, onSubmit, params, routerPush, suburbId, suburbName, locale])

  useEffect(() => { handleSubmit() }, [handleSubmit])

  // 添加到访问记录
  useEffect(() => {
    pushRecord({ type: 'agents', param: apiParam })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-f1.4 font-medium">{ta('matchingMediationList')}</h2>
      <div className="mt-16 overflow-hidden max-w-[30rem] relative">
        <div className="flex gap-x-2 animate-agent-avatar-scroll relative">
          {avatars.map((avatar, index) => (
            <div
              key={avatar + index}
              className="w-12 h-12 bg-cf0 shrink-0 rounded-full flex items-center justify-center"
            >
              {avatar && (
                <AspectRatioCover
                  brand=""
                  className="object-cover rounded-full" 
                  src={avatar}
                  width={60}
                  height={60}
                  alt=""
                />
              )}
            </div>
          ))}
        </div>
        <span
          className="absolute inset-0" 
          style={{ background: 'linear-gradient(90deg, white, transparent 30%, transparent 70%, white)' }}
        />
      </div>
      <div className="text-f.9 flex flex-col gap-y-4 mt-10 min-h-[7rem]">
        <IntroItem delay={0.2} label={ta('recentlySoldHomes', { suburbName })} /> 
        <IntroItem delay={0.4} label={ta('listingLast12Month', { suburbName })} /> 
        <IntroItem delay={0.6} label={ta('comparisonOfSalesData')} /> 
      </div>
    </div>
  )
}

SubmitSearchParam.propTypes = {
  params: PropTypes.array,
  suburbName: PropTypes.string,
}
